import React, { useEffect, useState } from 'react';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useHeaderSearch } from '../../contexts/SearchContext';
import NotificationBell from '../common/NotificationBell';
import AnnouncementSpeaker from '../common/AnnouncementSpeaker';

function resolveProfilePhotoUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  const s = String(pathOrUrl);
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
  const base = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');
  return `${base}${s.startsWith('/') ? s : `/${s}`}`;
}

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { headerSearch, setHeaderSearch } = useHeaderSearch();
  const [profilePhotoFailed, setProfilePhotoFailed] = useState(false);

  const photoSrc = user?.profile_picture ? resolveProfilePhotoUrl(user.profile_picture) : '';
  const showProfilePhoto = Boolean(photoSrc) && !profilePhotoFailed;

  useEffect(() => {
    setProfilePhotoFailed(false);
  }, [user?.profile_picture, user?.email, user?.email_address]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  return (
    <div className="relative z-30 flex h-14 flex-shrink-0 bg-white border-b border-slate-200">
      {/* Mobile menu button */}
      <button
        type="button"
        className="px-3 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
        onClick={onMenuClick}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="flex flex-1 min-w-0 items-center justify-between px-3 md:px-4">
        {/* Search */}
        <div className="flex flex-1 min-w-0">
          <form className="flex w-full min-w-0 md:ml-0" onSubmit={handleSearch}>
            <label htmlFor="search-field" className="sr-only">
              Search
            </label>
            <div className="relative w-full min-w-0 text-gray-400 focus-within:text-gray-600">
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
              </div>
              <input
                id="search-field"
                className="block h-full w-full border-transparent py-1.5 pl-7 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:border-transparent focus:outline-none focus:placeholder-gray-400 focus:ring-0"
                placeholder="Search employees, teams, claims..."
                type="search"
                name="search"
                value={headerSearch}
                onChange={(e) => setHeaderSearch(e.target.value)}
              />
            </div>
          </form>
        </div>

        <div className="ml-2 flex flex-shrink-0 items-center gap-2 md:ml-3 md:gap-3">
          {/* Announcements */}
          <AnnouncementSpeaker />

          {/* Notifications */}
          <NotificationBell />

          {/* Settings */}
          <button
            type="button"
            onClick={() => navigate('/settings')}
            title="Settings"
            aria-label="Settings"
            className="inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-500 hover:text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>

          {/* Profile button - navigates directly to /profile (no dropdown) */}
          <button
            type="button"
            onClick={() => navigate('/profile')}
            title="Your Profile"
            aria-label="Your Profile"
            className="flex items-center bg-white rounded-full px-1.5 py-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            {showProfilePhoto ? (
              <img
                src={photoSrc}
                alt=""
                className="h-7 w-7 rounded-full object-cover bg-gray-200"
                onError={() => setProfilePhotoFailed(true)}
              />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-300">
                <UserIcon className="h-4 w-4 text-gray-600" />
              </div>
            )}
            <div className="ml-3 hidden xl:block text-left">
              <p className="text-sm font-medium text-gray-700">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </button>

          {/* Logout (separate icon, right next to profile) */}
          <button
            type="button"
            onClick={handleLogout}
            title="Sign out"
            aria-label="Sign out"
            className="inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;
